import React, { useEffect, useRef } from 'react';

import { Arrow } from 'icons/untitledui';

import useListboxKeyboardNavigation from 'shared/hooks/useListboxKeyboardNavigation';
import useClickOutside from 'shared/hooks/useClickOutside';
import useControllableOpen from 'shared/hooks/useControllableOpen';
import * as Styled from 'theme/components/SelectionDropdown.style';

export type BaseDropdownOption = {
  value: string;
  label: React.ReactNode;
  description?: React.ReactNode;
  disabled?: boolean;
};

type PopoverRenderProps = {
  listRef: React.RefObject<HTMLDivElement | null>;
  ariaLabel: string;
  optionElements: React.ReactNode;
};

interface BaseSelectionDropdownProps {
  ariaLabel: string;
  options: BaseDropdownOption[];
  displayValue: React.ReactNode;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  triggerRef: React.RefObject<HTMLButtonElement | null>;
  listRef?: React.RefObject<HTMLDivElement | null>;
  triggerStyleProps?: {
    $fullWidth?: boolean;
    $openUp?: boolean;
    $maxWidth?: number | null;
  };
  additionalClickOutsideRefs?: React.RefObject<HTMLElement | null>[];
  isSelected: (option: BaseDropdownOption) => boolean;
  onOptionSelect: (option: BaseDropdownOption) => void;
  closeOnSelect?: boolean;
  renderOptionCheck: (selected: boolean) => React.ReactNode;
  renderPopover: (props: PopoverRenderProps) => React.ReactNode;
  onOpenEffect?: (listRef: React.RefObject<HTMLDivElement | null>) => void;
}

export function BaseSelectionDropdown({
  ariaLabel,
  options,
  displayValue,
  isOpen,
  onOpenChange,
  triggerRef,
  listRef: externalListRef,
  triggerStyleProps,
  additionalClickOutsideRefs = [],
  isSelected,
  onOptionSelect,
  closeOnSelect = true,
  renderOptionCheck,
  renderPopover,
  onOpenEffect,
}: BaseSelectionDropdownProps): JSX.Element {
  const [open, setOpen] = useControllableOpen(isOpen, onOpenChange);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const internalListRef = useRef<HTMLDivElement | null>(null);
  const listRef = externalListRef ?? internalListRef;

  useClickOutside(
    [rootRef, ...additionalClickOutsideRefs],
    () => setOpen(false),
    open
  );

  useEffect(() => {
    if (!open) return;
    onOpenEffect?.(listRef);
  }, [open, onOpenEffect, listRef]);

  const { onTriggerKeyDown, createOptionKeyDownHandler } =
    useListboxKeyboardNavigation({
      open,
      setOpen,
      triggerRef,
      listRef,
      selectedOptionSelector: `[data-selection-option="true"][data-selected="true"]`,
    });

  const handleSelect = (opt: BaseDropdownOption) => {
    if (opt.disabled) return;
    onOptionSelect(opt);
    if (closeOnSelect) {
      setOpen(false);
      triggerRef.current?.focus();
    }
  };

  const optionElements = options.map((opt) => {
    const selected = isSelected(opt);
    return (
      <Styled.Option
        key={opt.value}
        type="button"
        role="option"
        aria-selected={selected}
        disabled={opt.disabled}
        tabIndex={opt.disabled ? -1 : 0}
        data-selection-option="true"
        data-selected={selected ? 'true' : 'false'}
        $selected={selected}
        $hasDescription={!!opt.description}
        onClick={(e) => {
          e.stopPropagation();
          handleSelect(opt);
        }}
        onKeyDown={createOptionKeyDownHandler(() => handleSelect(opt))}
      >
        <Styled.OptionContent>
          <span>{opt.label}</span>
          {opt.description && (
            <Styled.OptionDescription>
              {opt.description}
            </Styled.OptionDescription>
          )}
        </Styled.OptionContent>
        {renderOptionCheck(selected)}
      </Styled.Option>
    );
  });

  return (
    <Styled.Wrapper ref={rootRef}>
      <Styled.TriggerButton
        ref={triggerRef}
        type="button"
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        $open={open}
        {...triggerStyleProps}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        onKeyDown={onTriggerKeyDown}
      >
        <span>{displayValue}</span>
        <Styled.Chevron $open={open}>
          <Arrow.ChevronDown size={20} />
        </Styled.Chevron>
      </Styled.TriggerButton>

      {open ? renderPopover({ listRef, ariaLabel, optionElements }) : null}
    </Styled.Wrapper>
  );
}
